import { useEffect, useState } from "react";
import { useEmissionsStore } from "@/data/emissionsStore";
import { useAuth } from "@/contexts/AuthContext";
import { useEventMetricsStore } from "@/data/eventMetricsStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, Heart, Calendar, CheckCircle, Clock, XCircle, Edit, Save, X, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MediaProgram } from "@/data/mediaPrograms";

interface MediaEmissionsTrackingPanelProps {
  onEditProgram: (prog: MediaProgram) => void;
}

export const MediaEmissionsTrackingPanel = ({ onEditProgram }: MediaEmissionsTrackingPanelProps) => {
  const { user } = useAuth();
  const { emissions, fetchEmissions } = useEmissionsStore();
  const { metrics, views } = useEventMetricsStore();
  
  useEffect(() => {
    fetchEmissions(false);
  }, [fetchEmissions]);

  const myEmissions = emissions.filter(e => e.channel === user?.channelName || e.channel === user?.name || user?.role === 'MEDIA');

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'approved': return <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-0"><CheckCircle className="w-3 h-3 mr-1" /> Publiée</Badge>;
      case 'pending': return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-0"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-800 hover:bg-red-200 border-0"><XCircle className="w-3 h-3 mr-1" /> Rejetée</Badge>;
      case 'draft': return <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50"><Save className="w-3 h-3 mr-1" /> Brouillon</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border shadow-sm">
        <CardHeader className="bg-muted/30 border-b border-border">
          <CardTitle className="text-xl">Suivi de vos Émissions</CardTitle>
          <CardDescription>Consultez les statistiques et l'état de validation de vos émissions soumises.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {myEmissions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Aucune émission soumise pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted/50 text-muted-foreground uppercase text-xs">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Émission</th>
                    <th className="px-6 py-4 font-semibold">Date de diffusion</th>
                    <th className="px-6 py-4 font-semibold">Statut</th>
                    <th className="px-6 py-4 font-semibold text-center">Vues</th>
                    <th className="px-6 py-4 font-semibold text-center">Likes</th>
                    <th className="px-6 py-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {myEmissions.map((prog) => {
                    const title = prog.frTitle || prog.arTitle || prog.zghTitle || 'Sans titre';
                    const progViews = views[prog.id] || 0;
                    const progLikes = metrics[prog.id] || 0;
                    const isPendingModif = prog.isModified && prog.status === 'pending';

                    return (
                      <tr key={prog.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-foreground">{title}</div>
                          {prog.subject_fr && <div className="text-xs text-muted-foreground truncate max-w-[200px]">{prog.subject_fr}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-muted-foreground">
                            <Calendar className="w-4 h-4 mr-2" />
                            {prog.dateFormatted_fr || prog.dateFormatted_ar}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1 items-start">
                            {getStatusBadge(prog.status)}
                            {isPendingModif && (
                              <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px] uppercase">
                                Modif. en attente
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant="secondary" className="font-mono bg-blue-50 text-blue-700">
                            <Eye className="w-3 h-3 mr-1" /> {progViews}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant="secondary" className="font-mono bg-pink-50 text-pink-700">
                            <Heart className="w-3 h-3 mr-1" /> {progLikes}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end items-center gap-2">
                            {prog.liveUrl && (
                              <Button 
                                variant="destructive" 
                                size="sm" 
                                className="h-8"
                                onClick={() => window.open(prog.liveUrl, '_blank')}
                              >
                                <PlayCircle className="w-4 h-4 mr-1" /> Direct
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 text-primary hover:bg-primary/10 hover:text-primary"
                              onClick={() => onEditProgram(prog)}
                            >
                              <Edit className="w-4 h-4 mr-2" /> Modifier
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
